import { Language } from '@core/i18n/domain/language';

/**
 * Language-scoped cache keys — the SINGLE source of truth for every persisted
 * cache key in /v2. NO hard-coded storage keys may exist anywhere else.
 *
 * Keys keep the product's recognizable structure but are NAMESPACED under the
 * `tlb_v2_patient_portal_` prefix so they NEVER collide with the identically
 * named v1 keys (`talbinah-portal-translations-*` / `PatientTalbinahPortal*`),
 * which v1 persists in a different shape. This keeps /v2 fully isolated — it
 * never reads or overwrites v1 cache state:
 *
 *   translations            → `tlb_v2_patient_portal_talbinah-portal-translations-{lang}-storage`
 *   translations @start      → `tlb_v2_patient_portal_talbinah-portal-translations-{lang}-started-at`
 *   home content            → `tlb_v2_patient_portal_PatientTalbinahPortalMainPageHomeContent_{lang}`
 *   home content @start      → `tlb_v2_patient_portal_PatientTalbinahPortalMainPageHomeContentStartedAt_{lang}`
 *
 * Builders are pure functions of `Language`, so no call site hand-concatenates
 * a key (no stringly-typed drift, no `any`). Every language gets a fully
 * isolated key — Arabic, English, and any future language (ru, zh, …) never
 * share or overwrite each other's cache.
 */

/**
 * A branded cache key. It is a `string` at runtime, but the brand stops an
 * arbitrary string from being passed where a *built* cache key is expected —
 * the only way to obtain one is through the builders below.
 */
export type CacheKey = string & { readonly __brand: 'CacheKey' };

const asCacheKey = (value: string): CacheKey => value as CacheKey;

/**
 * v2 namespace — isolates these keys from the identically-structured v1 keys so
 * /v2 never shares or modifies v1 cache state. Change here to re-namespace ALL
 * v2 cache keys at once.
 */
export const CACHE_NAMESPACE = 'tlb_v2_patient_portal_';

/** Fixed (namespaced) key prefixes — exported for diagnostics / cache-busting sweeps. */
export const CACHE_KEY_PREFIXES = {
  translations: `${CACHE_NAMESPACE}talbinah-portal-translations-`,
  homeContent: `${CACHE_NAMESPACE}PatientTalbinahPortalMainPageHomeContent_`,
  homeContentStartedAt: `${CACHE_NAMESPACE}PatientTalbinahPortalMainPageHomeContentStartedAt_`,
} as const;

/** `tlb_v2_patient_portal_talbinah-portal-translations-{lang}-storage` */
export function translationsCacheKey(lang: Language): CacheKey {
  return asCacheKey(`${CACHE_KEY_PREFIXES.translations}${lang}-storage`);
}

/** `tlb_v2_patient_portal_talbinah-portal-translations-{lang}-started-at` */
export function translationsStartedAtCacheKey(lang: Language): CacheKey {
  return asCacheKey(`${CACHE_KEY_PREFIXES.translations}${lang}-started-at`);
}

/** `tlb_v2_patient_portal_PatientTalbinahPortalMainPageHomeContent_{lang}` */
export function homeContentCacheKey(lang: Language): CacheKey {
  return asCacheKey(`${CACHE_KEY_PREFIXES.homeContent}${lang}`);
}

/** `tlb_v2_patient_portal_PatientTalbinahPortalMainPageHomeContentStartedAt_{lang}` */
export function homeContentStartedAtCacheKey(lang: Language): CacheKey {
  return asCacheKey(`${CACHE_KEY_PREFIXES.homeContentStartedAt}${lang}`);
}

/** Grouped accessor for ergonomic imports (`CacheKeys.translations(lang)`). */
export const CacheKeys = {
  translations: translationsCacheKey,
  translationsStartedAt: translationsStartedAtCacheKey,
  homeContent: homeContentCacheKey,
  homeContentStartedAt: homeContentStartedAtCacheKey,
} as const;

/**
 * CACHE_KEYS — the canonical, domain-grouped key map. The public surface
 * every service/facade reaches for (so there are NO hard-coded storage keys):
 * builders are language-scoped, branded, and namespaced.
 *
 *   CACHE_KEYS.translations.byLanguage('ar') → tlb_v2_patient_portal_talbinah-portal-translations-ar-storage
 *   CACHE_KEYS.translations.startedAt('ar')  → tlb_v2_patient_portal_talbinah-portal-translations-ar-started-at
 *   CACHE_KEYS.homepage.content('en')        → tlb_v2_patient_portal_PatientTalbinahPortalMainPageHomeContent_en
 *   CACHE_KEYS.homepage.startedAt('ar')      → tlb_v2_patient_portal_PatientTalbinahPortalMainPageHomeContentStartedAt_ar
 */
export const CACHE_KEYS = {
  translations: {
    byLanguage: (language: Language): CacheKey => translationsCacheKey(language),
    startedAt: (language: Language): CacheKey => translationsStartedAtCacheKey(language),
  },
  homepage: {
    content: (language: Language): CacheKey => homeContentCacheKey(language),
    startedAt: (language: Language): CacheKey => homeContentStartedAtCacheKey(language),
  },
} as const;
